// src/views/reports/EstimatesReportView.js
// QUOTE SEAM 12a VERIFIER: qreport-5b8e2c64
//
// The Estimates report: win rate, open value by stage, follow-ups, and why
// estimates closed. EVERY number is computed by buildEstimatesReport
// (src/utils/quoteReporting.js) — this file loads data and draws tables.
//
// WHO SEES WHAT (founder decision, 2026-10-02):
//   * Owner / Admin / Manager — the whole org, with a salesperson filter.
//   * Sales Consultant — only estimates where they are the salesperson. The
//     narrowing happens inside the helper, so every section and every CSV is
//     already scoped. In that mode this screen never loads the users list, so
//     no other rep's name can reach it.
//   * Anyone else — sent home (the route guard already stops them).
// DISPLAY-LEVEL ONLY: firestore.rules lets any org member read quotes and
// quoteCloses. Logged in BACKLOG; not changed by this seam.
//
// Read-only. Opens listeners on: the org doc (time zone + terminology), the
// quotes collection, the quoteCloses collection, settings/pipeline, and — full
// access only — the org's users (for salesperson names). No new query shape,
// no composite index.

import React, { useState, useEffect, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { ArrowLeft, Award, Download, DollarSign, Layers, Bell, HelpCircle, Users, Calendar } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { db } from '../../firebase/config';
import { formatCurrency } from '../../utils/formatters';
import { exportToCSV } from '../../utils/csvExport';
import { DOC_TYPES, resolveDocTerminology, ymdInTimeZone } from '../../utils/quoteSchema';
import usePipelineStages from '../../hooks/usePipelineStages';
import {
    buildEstimatesReport,
    resolveReportViewer,
    resolveReportRange,
    REPORT_RANGE_PRESETS,
    DEFAULT_REPORT_RANGE,
    REPORT_MIN_SAMPLE,
    UNASSIGNED_SALESPERSON,
} from '../../utils/quoteReporting';

const CLOSE_DATA_SINCE_LABEL = '30 Sep 2026';

const pct = (x) => (x === null || x === undefined ? '—' : `${Math.round(x * 100)}%`);
const oneDecimal = (x) => (x === null || x === undefined ? '—' : x.toFixed(1));
const monthLabel = (ym) => {
    const [y, m] = ym.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, 15)).toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const Card = ({ title, icon, children, onExport, note }) => (
    <div className="bg-white border rounded-lg shadow-sm overflow-hidden mb-6">
        <div className="p-4 border-b bg-gray-50 flex items-center justify-between gap-2">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">{icon}{title}</h3>
            {onExport && (
                <button onClick={onExport} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 bg-white rounded-md px-3 py-1">
                    <Download size={14} /> CSV
                </button>
            )}
        </div>
        {note && <p className="px-4 pt-3 text-xs text-gray-500">{note}</p>}
        <div className="p-4">{children}</div>
    </div>
);

const Tile = ({ label, value, sub, icon, to }) => {
    const body = (
        <div className="bg-white p-5 rounded-lg border shadow-sm h-full">
            <p className="text-sm text-gray-500 mb-1 flex items-center gap-2">{icon}{label}</p>
            <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
            {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
    );
    return to ? <Link to={to} className="block hover:shadow-md transition-shadow rounded-lg">{body}</Link> : body;
};

const Th = ({ children, right }) => <th className={`p-2 text-xs font-semibold text-gray-500 uppercase ${right ? 'text-right' : 'text-left'}`}>{children}</th>;
const Td = ({ children, right, bold }) => <td className={`p-2 ${right ? 'text-right tabular-nums' : ''} ${bold ? 'font-semibold' : ''}`}>{children}</td>;

const NotEnough = ({ count, what }) => (
    <p className="text-sm text-gray-500 bg-gray-50 border rounded-md p-3">
        Not enough closed estimates yet to show {what} ({count} so far; {REPORT_MIN_SAMPLE} needed).
    </p>
);

const EstimatesReportView = ({ currentUserProfile }) => {
    const orgId = currentUserProfile?.organizationId;
    const viewer = useMemo(() => resolveReportViewer(currentUserProfile), [currentUserProfile]);
    const isFull = viewer.access === 'full';

    const [orgDoc, setOrgDoc] = useState(null);
    const [quotes, setQuotes] = useState(null);
    const [closes, setCloses] = useState(null);
    const [users, setUsers] = useState([]);
    const stages = usePipelineStages(orgId);

    const [preset, setPreset] = useState(DEFAULT_REPORT_RANGE);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [salesFilter, setSalesFilter] = useState('');

    useEffect(() => {
        if (!orgId || viewer.access === 'denied') return undefined;
        const unsubOrg = onSnapshot(doc(db, `organizations/${orgId}`), (d) => setOrgDoc(d.exists() ? d.data() : {}),
            (err) => { console.warn('EstimatesReportView: org doc failed', err); setOrgDoc({}); });
        const unsubQuotes = onSnapshot(collection(db, `organizations/${orgId}/quotes`),
            (snap) => setQuotes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
            (err) => { console.warn('EstimatesReportView: quotes failed', err); setQuotes([]); });
        const unsubCloses = onSnapshot(collection(db, `organizations/${orgId}/quoteCloses`),
            (snap) => setCloses(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
            (err) => { console.warn('EstimatesReportView: quoteCloses failed', err); setCloses([]); });
        // Names only matter when you can see more than yourself.
        let unsubUsers = () => {};
        if (isFull) {
            unsubUsers = onSnapshot(query(collection(db, 'users'), where('organizationId', '==', orgId)),
                (snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
                (err) => { console.warn('EstimatesReportView: users failed', err); setUsers([]); });
        }
        return () => { unsubOrg(); unsubQuotes(); unsubCloses(); unsubUsers(); };
    }, [orgId, viewer.access, isFull]);

    const timeZone = orgDoc?.timezone;
    const { word, wordPlural } = resolveDocTerminology(orgDoc?.quoteTerminologyDefault || DOC_TYPES.QUOTE);
    const todayYmd = useMemo(() => ymdInTimeZone(new Date(), timeZone), [timeZone]);
    const range = useMemo(
        () => resolveReportRange(preset, todayYmd, { startYmd: customStart, endYmd: customEnd }),
        [preset, todayYmd, customStart, customEnd]
    );

    const report = useMemo(() => buildEstimatesReport({
        quotes: quotes || [],
        closes: closes || [],
        stages,
        timeZone,
        range,
        viewer,
        salesPersonFilter: isFull ? (salesFilter || null) : null,
    }), [quotes, closes, stages, timeZone, range, viewer, isFull, salesFilter]);

    // Full access only: the salesperson picker and the per-rep table's names.
    const nameOf = useMemo(() => {
        const m = new Map(users.map((u) => [u.id, u.name || u.email || 'Unknown']));
        return (id) => (id === UNASSIGNED_SALESPERSON ? 'Unassigned' : (m.get(id) || 'Unknown'));
    }, [users]);
    const salesOptions = useMemo(() => {
        if (!isFull || !quotes) return [];
        const full = buildEstimatesReport({ quotes, closes: [], stages, timeZone, range: { startYmd: null, endYmd: null }, viewer });
        return (full.salespeople || []).map((s) => ({ id: s.salesPersonId, name: nameOf(s.salesPersonId) }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [isFull, quotes, stages, timeZone, viewer, nameOf]);

    const stageLabel = useMemo(() => {
        const m = new Map((stages || []).map((s) => [s.id, s.label]));
        return (id) => m.get(id) || 'Unknown stage';
    }, [stages]);

    if (viewer.access === 'denied') return <Navigate to="/" replace />;
    if (!quotes || !closes || !stages || !orgDoc) {
        return <div className="p-10 text-center text-gray-500">Loading {wordPlural.toLowerCase()}…</div>;
    }

    const w = report.winRate;
    const c = report.closes;
    const rangeLabel = range.startYmd || range.endYmd ? `${range.startYmd || 'start'}_to_${range.endYmd || 'today'}` : 'all_time';
    const fileBase = `${wordPlural}_${isFull ? '' : 'mine_'}${rangeLabel}`;

    const exportMonths = () => exportToCSV(report.months.map((m) => ({
        Month: m.month, Won: m.won, Lost: m.lost, Deferred: m.deferred, Still_open: m.open,
        Win_rate: m.winRate === null ? '' : (m.winRate * 100).toFixed(1), Won_value: m.wonValue.toFixed(2),
    })), `${fileBase}_win_rate.csv`);
    const exportOpen = () => exportToCSV(report.openValue.stages.map((s) => ({
        Stage: s.label, Count: s.count, Value: s.total.toFixed(2),
    })), `${wordPlural}_open_value_by_stage.csv`);
    const exportReps = () => exportToCSV((report.salespeople || []).map((s) => ({
        Salesperson: nameOf(s.salesPersonId), Won: s.won, Lost: s.lost, Deferred: s.deferred, Still_open: s.open,
        Win_rate: s.winRate === null ? '' : (s.winRate * 100).toFixed(1), Won_value: s.wonValue.toFixed(2),
    })), `${fileBase}_by_salesperson.csv`);
    const exportReasons = () => {
        const rows = [];
        for (const [outcome, table] of [['Won', c.reasonsWon], ['Lost', c.reasonsLost]]) {
            for (const r of table.rows) {
                for (const sid of table.stageIds) {
                    if (r.counts[sid]) rows.push({ Outcome: outcome, Reason: r.reason, Stage_when_it_closed: stageLabel(sid), Count: r.counts[sid] });
                }
            }
        }
        exportToCSV(rows, `${fileBase}_close_reasons.csv`);
    };
    const exportPrice = () => exportToCSV(c.priceFeedback.map((p) => ({
        Price_feedback: p.value, Won: p.won, Lost: p.lost, Deferred: p.deferred, Total: p.total,
    })), `${fileBase}_price_feedback.csv`);

    const chartData = report.months.map((m) => ({ name: monthLabel(m.month), Won: m.won, Lost: m.lost }));

    // Won and Lost use the SAME stage columns (c.reasonStageIds), the same
    // fixed widths and the same minimum width, so they line up column for
    // column. A table with no closes still renders, as a "none" row.
    const stageCols = c.reasonStageIds || [];
    const REASON_W = 34, TOTAL_W = 12;
    const stageW = stageCols.length ? (100 - REASON_W - TOTAL_W) / stageCols.length : 0;
    const reasonMinWidth = 220 + stageCols.length * 110 + 80;
    const ReasonTable = ({ title, table }) => (
        <div className="mb-6">
            <h4 className="font-semibold text-gray-700 mb-2">{title}</h4>
            <div className="overflow-x-auto">
                <table className="w-full table-fixed text-sm" style={{ minWidth: reasonMinWidth }}>
                    <colgroup>
                        <col style={{ width: `${REASON_W}%` }} />
                        {stageCols.map((sid) => <col key={sid} style={{ width: `${stageW}%` }} />)}
                        <col style={{ width: `${TOTAL_W}%` }} />
                    </colgroup>
                    <thead className="bg-gray-50">
                        <tr>
                            <th rowSpan={2} className="p-2 text-xs font-semibold text-gray-500 uppercase text-left align-bottom">Reason</th>
                            {stageCols.length > 0 && (
                                <th colSpan={stageCols.length} className="p-2 text-xs font-semibold text-gray-500 uppercase text-center border-b border-gray-200">
                                    Stage when it closed
                                </th>
                            )}
                            <th rowSpan={2} className="p-2 text-xs font-semibold text-gray-500 uppercase text-right align-bottom">Total</th>
                        </tr>
                        <tr>
                            {stageCols.map((sid) => (
                                <th key={sid} className="p-2 text-xs font-semibold text-gray-600 text-right truncate" title={stageLabel(sid)}>{stageLabel(sid)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {table.rows.length === 0 ? (
                            <tr>
                                <Td>None in this range</Td>
                                {stageCols.map((sid) => <Td key={sid} right>0</Td>)}
                                <Td right bold>0</Td>
                            </tr>
                        ) : table.rows.map((r) => (
                            <tr key={r.reason}>
                                <Td>{r.reason}</Td>
                                {stageCols.map((sid) => <Td key={sid} right>{r.counts[sid] || 0}</Td>)}
                                <Td right bold>{r.total}</Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const DaysRow = ({ label, d }) => (
        <tr>
            <Td>{label}</Td>
            <Td right>{d.total.n ? oneDecimal(d.total.avg) : '—'}</Td>
            <Td right>{d.active.n ? oneDecimal(d.active.avg) : '—'}</Td>
            <Td right>{d.total.n}</Td>
        </tr>
    );

    return (
        <div className="p-4 md:p-10 bg-gray-50 min-h-screen">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
                <div className="flex items-center gap-4">
                    <Link to={isFull ? '/reports' : '/'} className="p-2 bg-white border rounded-full hover:bg-gray-100">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">{isFull ? `${wordPlural} Report` : `My ${wordPlural} Report`}</h1>
                        <p className="text-gray-500 text-sm">
                            {isFull ? 'Win rate, open value and why deals closed' : `Only ${wordPlural.toLowerCase()} where you are the salesperson`}
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 bg-white p-2 rounded-md border shadow-sm">
                        <Calendar size={18} className="text-gray-400" />
                        <select value={preset} onChange={(e) => setPreset(e.target.value)} aria-label="Date range" className="text-sm border-none focus:ring-0 text-gray-700 bg-transparent">
                            {REPORT_RANGE_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                    </div>
                    {preset === 'custom' && (
                        <div className="flex items-center gap-2 bg-white p-2 rounded-md border shadow-sm">
                            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="text-sm border-none focus:ring-0 text-gray-600" aria-label="From" />
                            <span className="text-gray-400">-</span>
                            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="text-sm border-none focus:ring-0 text-gray-600" aria-label="To" />
                        </div>
                    )}
                    {isFull && (
                        <select value={salesFilter} onChange={(e) => setSalesFilter(e.target.value)} aria-label="Filter by salesperson"
                            className="py-2 px-3 text-sm border border-gray-300 rounded-md bg-white shadow-sm">
                            <option value="">All salespeople</option>
                            {salesOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                    )}
                </div>
            </div>
            <p className="text-xs text-gray-500 mb-4">
                Dates are in the company time zone ({timeZone || 'America/Toronto'}).
            </p>

            {/* TILES */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Tile label="Win rate" icon={<Award size={16} />} value={pct(w.winRate)}
                    sub={`${w.won} won / ${w.lost} lost · ${w.deferred} deferred · ${w.open} still open${w.decided > 0 && w.decided < REPORT_MIN_SAMPLE ? ' · small sample' : ''}`} />
                <Tile label="Won value" icon={<DollarSign size={16} />} value={formatCurrency(w.wonValue)}
                    sub={`${w.won} ${w.won === 1 ? word.toLowerCase() : wordPlural.toLowerCase()} won in this range`} />
                <Tile label="Open value (right now)" icon={<Layers size={16} />} value={formatCurrency(report.openValue.total)}
                    sub={`${report.openValue.count} open · incl. ${formatCurrency(report.openValue.draftTotal)} in drafts`} />
                <Tile label="Follow-ups" icon={<Bell size={16} />} value={`${report.followUps.overdue} overdue`}
                    sub={`${report.followUps.dueToday} due today · tap to see what needs attention`} to="/quotes?attention=1" />
            </div>

            {/* WIN RATE BY MONTH */}
            <Card title="Win rate by month sent" icon={<Award size={18} className="text-green-600" />} onExport={report.months.length ? exportMonths : null}
                note={`Grouped by the date each ${word.toLowerCase()} was sent (re-sending moves it to the later month). Drafts aren't counted. Won and lost come from the pipeline stage; deferred and still-open ${wordPlural.toLowerCase()} are shown but not in the win rate, so it can never pass 100%.`}>
                {report.months.length === 0 ? (
                    <p className="text-sm text-gray-500">No {wordPlural.toLowerCase()} were sent in this range.</p>
                ) : (
                    <>
                        <div className="h-64 w-full mb-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                                    <YAxis fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                                    <RechartsTooltip cursor={{ fill: 'transparent' }} />
                                    <Legend />
                                    <Bar dataKey="Won" stackId="o" fill="#22c55e" />
                                    <Bar dataKey="Lost" stackId="o" fill="#ef4444" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50"><tr>
                                    <Th>Month</Th><Th right>Won</Th><Th right>Lost</Th><Th right>Deferred</Th><Th right>Still open</Th><Th right>Win rate</Th><Th right>Won value</Th>
                                </tr></thead>
                                <tbody className="divide-y">
                                    {report.months.map((m) => (
                                        <tr key={m.month}>
                                            <Td>{monthLabel(m.month)}</Td><Td right>{m.won}</Td><Td right>{m.lost}</Td><Td right>{m.deferred}</Td>
                                            <Td right>{m.open}</Td><Td right bold>{pct(m.winRate)}</Td><Td right>{formatCurrency(m.wonValue)}</Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </Card>

            {/* OPEN VALUE BY STAGE */}
            <Card title="Open value by stage (right now)" icon={<Layers size={18} className="text-blue-600" />} onExport={exportOpen}
                note={`Every open ${word.toLowerCase()}, whatever the date range. Same totals as the board.`}>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50"><tr><Th>Stage</Th><Th right>Count</Th><Th right>Value</Th></tr></thead>
                        <tbody className="divide-y">
                            {report.openValue.stages.map((s) => (
                                <tr key={s.stageId}><Td>{s.label}</Td><Td right>{s.count}</Td><Td right>{formatCurrency(s.total)}</Td></tr>
                            ))}
                            <tr className="bg-gray-50"><Td bold>Total open</Td><Td right bold>{report.openValue.count}</Td><Td right bold>{formatCurrency(report.openValue.total)}</Td></tr>
                        </tbody>
                    </table>
                </div>
                {report.openValue.unplacedCount > 0 && (
                    <p className="text-xs text-gray-500 mt-2">{report.openValue.unplacedCount} {wordPlural.toLowerCase()} are on a stage that no longer exists and aren't counted here.</p>
                )}
            </Card>

            {/* BY SALESPERSON — full access only */}
            {isFull && report.salespeople && (
                <Card title="By salesperson" icon={<Users size={18} className="text-purple-600" />} onExport={report.salespeople.length ? exportReps : null}
                    note={`The ${word.toLowerCase()}'s salesperson: the customer's assigned rep, or whoever wrote it when there is none.`}>
                    {report.salespeople.length === 0 ? (
                        <p className="text-sm text-gray-500">No {wordPlural.toLowerCase()} were sent in this range.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50"><tr>
                                    <Th>Salesperson</Th><Th right>Won</Th><Th right>Lost</Th><Th right>Deferred</Th><Th right>Still open</Th><Th right>Win rate</Th><Th right>Won value</Th>
                                </tr></thead>
                                <tbody className="divide-y">
                                    {report.salespeople.map((s) => (
                                        <tr key={s.salesPersonId}>
                                            <Td>{nameOf(s.salesPersonId)}</Td><Td right>{s.won}</Td><Td right>{s.lost}</Td><Td right>{s.deferred}</Td>
                                            <Td right>{s.open}</Td><Td right bold>{pct(s.winRate)}</Td><Td right>{formatCurrency(s.wonValue)}</Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            )}

            {/* WHY ESTIMATES CLOSED — quoteCloses only */}
            <Card title={`Why ${wordPlural.toLowerCase()} closed`} icon={<HelpCircle size={18} className="text-orange-500" />}
                onExport={c.count ? exportReasons : null}
                note={`Columns show which stage each ${word.toLowerCase()} was in when it was won or lost. Recorded since ${CLOSE_DATA_SINCE_LABEL}.`}>
                {c.rangeStartsBeforeData && c.decidedWithoutDetails > 0 && (
                    <p className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                        Close details aren't available before {CLOSE_DATA_SINCE_LABEL}: {c.decidedWithoutDetails} decided {c.decidedWithoutDetails === 1 ? word.toLowerCase() : wordPlural.toLowerCase()} in this range closed earlier and have no reason recorded.
                    </p>
                )}
                {c.count === 0 ? (
                    <p className="text-sm text-gray-500">No {wordPlural.toLowerCase()} closed in this range yet.</p>
                ) : (
                    <>
                        <p className="text-sm text-gray-600 mb-4">
                            {c.count} closed in this range ({c.won} won, {c.lost} lost, {c.deferred} deferred) · {c.answered} with an answer.
                        </p>
                        {!c.sampleOk && <div className="mb-4"><NotEnough count={c.count} what="percentages" /></div>}
                        <ReasonTable title="Lost — reasons" table={c.reasonsLost} />
                        <ReasonTable title="Won — reasons" table={c.reasonsWon} />
                    </>
                )}
            </Card>

            {/* PRICE FEEDBACK */}
            <Card title="Price feedback" icon={<DollarSign size={18} className="text-green-600" />} onExport={c.count ? exportPrice : null}
                note={`How our number landed against the competition. Recorded since ${CLOSE_DATA_SINCE_LABEL}.`}>
                {c.count === 0 ? (
                    <p className="text-sm text-gray-500">No {wordPlural.toLowerCase()} closed in this range yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50"><tr>
                                <Th>Price feedback</Th><Th right>Won</Th><Th right>Lost</Th><Th right>Deferred</Th><Th right>Total</Th>{c.sampleOk && <Th right>Share</Th>}
                            </tr></thead>
                            <tbody className="divide-y">
                                {c.priceFeedback.map((p) => (
                                    <tr key={p.value}>
                                        <Td>{p.value}</Td><Td right>{p.won}</Td><Td right>{p.lost}</Td><Td right>{p.deferred}</Td><Td right bold>{p.total}</Td>
                                        {c.sampleOk && <Td right>{pct(p.total / c.count)}</Td>}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* TIME TO CLOSE */}
            <Card title="Time to close" icon={<Calendar size={18} className="text-gray-600" />}
                note={`Days from sending to closing. "Active" leaves out time the ${word.toLowerCase()} was snoozed. Recorded since ${CLOSE_DATA_SINCE_LABEL}.`}>
                {!c.sampleOk ? (
                    <NotEnough count={c.count} what="averages" />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50"><tr><Th>Outcome</Th><Th right>Avg days</Th><Th right>Avg active days</Th><Th right>Closes</Th></tr></thead>
                            <tbody className="divide-y">
                                <DaysRow label="Won" d={c.daysWon} />
                                <DaysRow label="Lost" d={c.daysLost} />
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default EstimatesReportView;
